import type { UiNotificationService } from '../ui/notification';

import { leadApi, LeadStatus, NewLeadProps, projectApi } from '../services/api';
import { contactsChecker } from '../services/contactsChecker';
import { logger } from '../services/logger';

interface StateParams {
  phone: null;
  phoneOrEmail: null;
  mobile?: null;
}

interface SelectorItem {
  name: string;
  id: string;
  selected: boolean;
}

export class CreateNewLeadPopupCtrl {
  static $inject = [
    '$scope',
    '$stateParams',
    '$state',
    '$cookieStore',
    '$window',
    '$timeout',
    '$notification',
    'localStorageService',
    '$translate',
  ];

  declare phoneOrEmail: string | null;
  declare mobile: null;

  loaded = false;
  submitIsDisabled = false;
  lead: Omit<NewLeadProps, 'status_id' | 'custom_note'> & { id?: string } = {
    name: '',
    objects: [],
    tags: [],
    note: '',
    phone: '',
    email: '',
    custom_fields: [],
    contacts: [],
    ad_source_id: null,
  };

  statusId = '';
  leadEmail = '';
  leadPhone = '';
  note = {
    message: '',
  };
  commonProject = {};
  projects: SelectorItem[] = [];
  itemsOfInterest: SelectorItem[] = [];
  leadStatuses: LeadStatus[] = [];
  selectedProjects: { [key: string]: string }[] = [];
  selectedItems: { [key: string]: string }[] = [];

  constructor(
    private $scope: angular.IScope,
    $stateParams: StateParams,
    private $state,
    private $cookieStore,
    private $window: angular.IWindowService,
    private $timeout: angular.ITimeoutService,
    private $notification: UiNotificationService,
    private localStorageService,
    private $translate: angular.translate.ITranslateService,
  ) {
    if ($cookieStore.get('project')) {
      this.commonProject = $cookieStore.get('project');
    }

    this.phoneOrEmail = $stateParams.phoneOrEmail;

    if (this.phoneOrEmail) {
      if (contactsChecker.hasEmail(this.phoneOrEmail)) {
        this.leadEmail = this.phoneOrEmail;
      } else {
        const phoneFromIPTelephony = $stateParams.phone;
        this.leadPhone = this.phoneOrEmail || phoneFromIPTelephony;
      }
    }

    Promise.all([
      projectApi.getAll().then((projects) => {
        const projectsArr: SelectorItem[] = [];

        projects.forEach((project) => {
          projectsArr.push({
            name: project.name,
            id: project.id,
            selected: false,
          });
        });

        this.projects = projectsArr;
      }),
      leadApi.getTags().then((tags) => {
        const tagsArr: SelectorItem[] = [];

        tags.forEach((tag) => {
          tagsArr.push({
            name: tag.name,
            id: tag.id,
            selected: false,
          });
        });

        this.itemsOfInterest = tagsArr;
      }),
      leadApi.getStatuses().then((leadStatuses) => {
        this.leadStatuses = leadStatuses;
      }),
    ])
      .then(() => {
        this.loaded = true;
        $scope.$apply();
      })
      .catch((error) => {
        logger.error('Error loading lead', {
          error,
        });
      });
  }

  async createLead(isRedirected) {
    const isRedirectSet = isRedirected || false;

    if (this.submitIsDisabled) {
      return;
    }

    this.$scope.$apply();
    this.submitIsDisabled = true;

    this.prepareObjectsAndTags();

    const data: NewLeadProps = {
      name: this.lead.name,
      objects: this.lead.objects,
      tags: this.lead.tags,
      note: this.lead.note,
      phone: this.lead.phone,
      email: this.lead.email,
      contacts: this.lead.contacts,
      status_id: this.statusId,
      custom_fields: this.lead.custom_fields,
      custom_note: this.note.message,
      ad_source_id: this.lead.ad_source_id || null,
    };

    try {
      const response = await leadApi.create(data);

      if (isRedirectSet) {
        // Also save to ls
        this.lead.id = response.id;
        this.localStorageService.set('lead', this.lead);

        this.$timeout(() => {
          this.$window.location.href = '/';
        }, 300);
      } else {
        this.closeCreateNewLeadPopup();

        this.$notification.show({
          type: 'success',
          text: this.$translate.instant('leads.mobile.add.toast.msg'),
        });
      }
    } catch (error) {
      logger.error('Error creating lead', {
        error,
        data,
      });
    }
  }

  prepareObjectsAndTags() {
    if (!this.selectedProjects && !this.selectedItems) {
      return;
    }

    const objectsArr: { [key: string]: string }[] = [];

    if (this.selectedProjects) {
      this.selectedProjects.forEach((project) => {
        objectsArr.push({
          [project.id]: project.name,
        });
      });

      this.lead.objects = objectsArr;
    }

    const tagsArr: { [key: string]: string }[] = [];

    if (this.selectedItems) {
      this.selectedItems.forEach((item) => {
        tagsArr.push({
          [item.id]: item.name,
        });
      });

      this.lead.tags = tagsArr;
    }
  }

  closeCreateNewLeadPopup() {
    this.$state.go('^');
  }

  markInMultiSelect(model: SelectorItem[], id: string | number) {
    model.forEach((item) => {
      if (String(item.id) === String(id)) {
        item.selected = true;
      }
    });
  }

  addProjectToCookie(project) {
    let cookieProject = null;

    if (this.$cookieStore.get('project')) {
      cookieProject = this.$cookieStore.get('project');
    }

    if (
      project.selected &&
      this.selectedProjects.length === 1 &&
      !cookieProject
    ) {
      this.$cookieStore.put('project', project);
    }
  }
}
